// WeightDemo.jsx
import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faNoteSticky, faChartLine, faListCheck } from '@fortawesome/free-solid-svg-icons';
import {
  TrackingOverviewCard,
  TrackingOverviewFooter,
  TrackingOverviewFooterLabel,
  TrackingOverviewFooterValue,
  TrackingOverviewTitle,
  TrackingPeriodTab,
  TrackingPeriodTabs,
  TrackingSessionActions,
  TrackingSessionCard,
  TrackingSessionHeader,
  TrackingSessionList,
  TrackingSessionMetaItem,
  TrackingSessionMetaRow,
  TrackingSessionScore,
  TrackingSessionScoreLabel,
  TrackingSessionScoreValue,
  TrackingSessionsCard,
  TrackingSessionsTitle,
  TrackingSessionTitle,
} from '../../common/TrackingShared';
import {
  colors,
  EntryPanel,
  EntryPanelHeader,
  EntryPanelTitle,
  EntryPanelHint,
  Grid,
  Field,
  Label,
  Input,
  EntryDatePicker,
  NoteEditorForm,
  NoteFormGroup,
  NoteLabel,
  NoteLabelIcon,
  NoteLabelText,
  NoteTextarea,
  SessionActionButton,
  SessionMain,
  SessionNote,
  SessionNoteLabel,
  SessionNoteText,
} from '../../sleep/SleepJournal.styled';
import { FullWidthSection, FeatureRow, FeatureRowText, FeatureRowVisual } from './DemoFeatureRow';
import useProgressiveEntry from './useProgressiveEntry';

/* ─── Graphique courbe lisse (inchangé) ─── */
const ChartWrapper = styled.div`
  position: relative;
  width: 100%;
  user-select: none;
`;

const ChartSvg = styled.svg`
  width: 100%;
  overflow: visible;
  display: block;
`;

const TooltipBox = styled.div`
  position: absolute;
  background: var(--bleu-mineral, #1A2E40);
  color: #ffffff;
  border-radius: 8px;
  padding: 0.35rem 0.6rem;
  font-size: 0.75rem;
  font-weight: 700;
  pointer-events: none;
  white-space: nowrap;
  transform: translate(-50%, -110%);
  z-index: 10;
  box-shadow: 0 4px 12px rgba(26, 46, 64, 0.22);

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: var(--bleu-mineral, #1A2E40);
  }
`;

const CHART_H = 140;
const CHART_PAD = { top: 18, right: 16, bottom: 28, left: 38 };

const buildSmoothPath = (points) => {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const curr = points[i];
    const next = points[i + 1];
    const cpx = (curr.x + next.x) / 2;
    d += ` C ${cpx} ${curr.y}, ${cpx} ${next.y}, ${next.x} ${next.y}`;
  }
  return d;
};

const WeightLineChart = ({ series }) => {
  const [tooltip, setTooltip] = useState(null);
  const wrapperRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(320);

  useEffect(() => {
    if (!wrapperRef.current) return;
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setChartWidth(w);
    });
    observer.observe(wrapperRef.current);
    setChartWidth(wrapperRef.current.offsetWidth || 320);
    return () => observer.disconnect();
  }, []);

  const innerW = chartWidth - CHART_PAD.left - CHART_PAD.right;
  const innerH = CHART_H - CHART_PAD.top - CHART_PAD.bottom;

  const values = series.map((d) => d.value);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const valRange = maxVal - minVal || 1;

  const toX = (i) =>
    series.length === 1
      ? CHART_PAD.left + innerW / 2
      : CHART_PAD.left + (i / (series.length - 1)) * innerW;

  const toY = (v) => CHART_PAD.top + innerH - ((v - minVal) / valRange) * innerH;

  const points = series.map((d, i) => ({ x: toX(i), y: toY(d.value), ...d }));
  const linePath = buildSmoothPath(points);
  const areaPath =
    series.length > 1
      ? `${linePath} L ${points[points.length - 1].x} ${CHART_PAD.top + innerH} L ${points[0].x} ${CHART_PAD.top + innerH} Z`
      : '';

  const yTicks = [minVal, (minVal + maxVal) / 2, maxVal];

  const handleMouseEnter = (pt) => {
    setTooltip({ x: pt.x, y: pt.y, label: pt.label, value: pt.value });
  };
  const handleMouseLeave = () => setTooltip(null);

  return (
    <ChartWrapper ref={wrapperRef}>
      <ChartSvg
        viewBox={`0 0 ${chartWidth} ${CHART_H}`}
        height={CHART_H}
        aria-label={`Courbe de poids — ${series.map((d) => `${d.label}: ${d.value.toFixed(1)} kg`).join(', ')}`}
        role="img"
      >
        <defs>
          <linearGradient id="wgt-area-grad-demo" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1A2E40" stopOpacity="0.13" />
            <stop offset="100%" stopColor="#1A2E40" stopOpacity="0.01" />
          </linearGradient>
          <linearGradient id="wgt-line-grad-demo" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#2c5282" />
            <stop offset="100%" stopColor="#1A2E40" />
          </linearGradient>
        </defs>

        {yTicks.map((tick, i) => {
          const y = toY(tick);
          return (
            <g key={i}>
              <line
                x1={CHART_PAD.left}
                x2={CHART_PAD.left + innerW}
                y1={y}
                y2={y}
                stroke="#E1E8ED"
                strokeWidth="1"
                strokeDasharray={i === 0 || i === yTicks.length - 1 ? '0' : '4 3'}
              />
              <text
                x={CHART_PAD.left - 6}
                y={y + 4}
                textAnchor="end"
                fontSize="10"
                fill="#999999"
                fontWeight="600"
              >
                {tick.toFixed(1)}
              </text>
            </g>
          );
        })}

        {areaPath && <path d={areaPath} fill="url(#wgt-area-grad-demo)" />}

        <path
          className="weight-demo-chart-line"
          d={linePath}
          pathLength={1}
          fill="none"
          stroke="url(#wgt-line-grad-demo)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {points.map((pt, i) => (
          <text
            key={pt.id || i}
            x={pt.x}
            y={CHART_H - 6}
            textAnchor="middle"
            fontSize="10"
            fill="#999999"
            fontWeight="600"
          >
            {pt.label}
          </text>
        ))}

        {points.map((pt, i) => (
          <g key={`pt-${pt.id || i}`}>
            <circle
              cx={pt.x}
              cy={pt.y}
              r="8"
              fill="transparent"
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => handleMouseEnter(pt)}
              onMouseLeave={handleMouseLeave}
              onFocus={() => setTooltip({ x: pt.x, y: pt.y, label: pt.label, value: pt.value })}
              onBlur={handleMouseLeave}
              tabIndex={0}
              aria-label={`${pt.label}: ${pt.value.toFixed(1)} kg`}
            />
            <circle
              cx={pt.x}
              cy={pt.y}
              r={tooltip?.label === pt.label ? 5 : 3.5}
              fill="#ffffff"
              stroke="#1A2E40"
              strokeWidth="2"
              style={{ pointerEvents: 'none', transition: 'r 0.15s' }}
            />
          </g>
        ))}
      </ChartSvg>

      {tooltip && (
        <TooltipBox style={{ left: tooltip.x, top: tooltip.y + CHART_PAD.top }}>
          {tooltip.label} — {tooltip.value.toFixed(1)} kg
        </TooltipBox>
      )}
    </ChartWrapper>
  );
};

// ---- Helpers ----
const formatKg = (value) => (Number.isFinite(value) ? `${value.toFixed(1)} kg` : '—');

const formatSignedKg = (value) => {
  if (!Number.isFinite(value)) return '—';
  const normalized = Math.abs(value) < 0.05 ? 0 : value;
  const sign = normalized > 0 ? '+' : '';
  return `${sign}${normalized.toFixed(1)} kg`;
};

const modeLabel = (mode) => (mode === 'lose' ? 'Perte' : mode === 'gain' ? 'Prise' : 'Maintien');

const computeWeightScore = (currentWeight, targetWeight) => {
  if (!Number.isFinite(currentWeight) || !Number.isFinite(targetWeight) || targetWeight <= 0) {
    return { value: null, color: 'var(--bleu-mineral)' };
  }
  const distance = Math.abs(currentWeight - targetWeight);
  const tolerance = Math.max(3, targetWeight * 0.12);
  const ratio = Math.max(0, 1 - distance / tolerance);
  const score = Math.round(ratio * 100);
  if (score >= 80) return { value: score, color: 'var(--vert-vitalite)' };
  return { value: score, color: 'var(--rouge-alerte)' };
};

const formatSessionTitle = (isoDate) => {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'short' });
};

// ---- Données mock ----
const TARGET_WEIGHT = 75;

const MOCK_WEEK = [
  { id: 'w1', label: 'ven', value: 84.0 },
  { id: 'w2', label: 'sam', value: 83.7 },
  { id: 'w3', label: 'dim', value: 83.5 },
  { id: 'w4', label: 'lun', value: 83.2 },
  { id: 'w5', label: 'mar', value: 83.0 },
  { id: 'w6', label: 'mer', value: 82.7 },
  { id: 'w7', label: 'jeu', value: 82.4 },
];
const weekAverage = MOCK_WEEK.reduce((s, d) => s + d.value, 0) / MOCK_WEEK.length;

const MOCK_SESSIONS = [
  { id: 's1', at: '2026-08-06T07:30:00', current: 82.4, previous: 82.9, note: '' },
  { id: 's2', at: '2026-08-04T07:20:00', current: 82.9, previous: 83.3, note: 'Pesée le matin à jeun.' },
  { id: 's3', at: '2026-08-02T07:15:00', current: 83.3, previous: null, note: '' },
];

// ---- Composant ----
const WeightDemo = () => {
  const [period, setPeriod] = useState('week');
  const currentWeightRef = useRef(null);
  const weightNoteRef = useRef(null);
  const currentWeight = useProgressiveEntry('82.4', { interval: 220, cycle: 8000, targetRef: currentWeightRef });
  const weightNote = useProgressiveEntry('Pesée après ma séance de sport.', { delay: 500, interval: 65, cycle: 8000, targetRef: weightNoteRef });

  return (
    <FullWidthSection>

      {/* Bloc 1 — Saisie rapide */}
      <FeatureRow>
        <FeatureRowText>
          <h3><FontAwesomeIcon icon={faNoteSticky} /> Une pesée en 10 secondes</h3>
          <p>
            Indiquez simplement votre poids du jour : l'écart avec votre
            objectif est calculé automatiquement, sans aucun calcul à faire
            de votre côté.
          </p>
          <p>
            Ajoutez une note optionnelle pour vous souvenir du contexte —
            pesée après le sport, rétention d'eau, ressenti du jour...
          </p>
        </FeatureRowText>
        <FeatureRowVisual className="demo-preview-visual">
          <EntryPanel>
            <EntryPanelHeader>
              <EntryPanelTitle>Nouvelle pesée</EntryPanelTitle>
              <EntryPanelHint>
                L'écart correspond à : poids actuel - poids cible.
              </EntryPanelHint>
            </EntryPanelHeader>

            <Grid>
              <Field>
                <Label>Poids actuel (kg)</Label>
                <Input
                  ref={currentWeightRef}
                  type="text"
                  inputMode="decimal"
                  placeholder="Ex: 82.5"
                  value={currentWeight}
                  aria-label="Poids actuel en kilogrammes"
                  readOnly
                />
              </Field>
              <Field>
                <Label>Date de la pesée</Label>
                <EntryDatePicker
                  mode="friend"
                  value="2026-08-06"
                  onChange={() => { }}
                  maxDateKey="2026-08-06"
                  ariaLabel="Date de l’entrée de poids"
                  dialogLabel="Calendrier pour choisir la date de la pesée"
                  showQuickActions
                />
              </Field>
            </Grid>

            <NoteEditorForm onSubmit={(e) => e.preventDefault()}>
              <NoteFormGroup>
                <NoteLabel htmlFor="weight-demo-note">
                  <NoteLabelIcon icon={faNoteSticky} />
                  <NoteLabelText>Note pour cette entrée</NoteLabelText>
                </NoteLabel>
                <NoteTextarea
                  ref={weightNoteRef}
                  id="weight-demo-note"
                  value={weightNote}
                  placeholder="Ajoutez une note (ex. : pesée après le sport, rétention d’eau, ressenti…)"
                  rows={4}
                  maxLength={500}
                  readOnly
                />
              </NoteFormGroup>
            </NoteEditorForm>
          </EntryPanel>
        </FeatureRowVisual>
      </FeatureRow>

      {/* Bloc 2 — Courbe de progression */}
      <FeatureRow $reverse>
        <FeatureRowText $reverse>
          <h3><FontAwesomeIcon icon={faChartLine} /> Une courbe qui parle</h3>
          <p>
            La vue hebdomadaire trace une courbe lisse de vos pesées et
            affiche votre poids moyen sur la période sélectionnée.
          </p>
          <p>
            Basculez entre semaine, mois et année pour prendre du recul
            sur votre progression, sans jamais perdre le fil de votre
            objectif.
          </p>
        </FeatureRowText>
        <FeatureRowVisual className="demo-preview-visual" $reverse>
          <TrackingPeriodTabs>
            {[
              { key: 'week', label: 'Semaine' },
              { key: 'month', label: 'Mois' },
              { key: 'year', label: 'Année' },
            ].map((item) => (
              <TrackingPeriodTab
                key={item.key}
                type="button"
                $active={period === item.key}
                onClick={() => setPeriod(item.key)}
              >
                {item.label}
              </TrackingPeriodTab>
            ))}
          </TrackingPeriodTabs>

          <TrackingOverviewCard>
            <TrackingOverviewTitle>Vue hebdomadaire</TrackingOverviewTitle>
            <WeightLineChart series={MOCK_WEEK} />
            <TrackingOverviewFooter>
              <TrackingOverviewFooterLabel>Poids moyen</TrackingOverviewFooterLabel>
              <TrackingOverviewFooterValue>{formatKg(weekAverage)}</TrackingOverviewFooterValue>
            </TrackingOverviewFooter>
          </TrackingOverviewCard>
        </FeatureRowVisual>
      </FeatureRow>

      {/* Bloc 3 — Historique des sessions */}
      <FeatureRow>
        <FeatureRowText>
          <h3><FontAwesomeIcon icon={faListCheck} /> Un historique complet</h3>
          <p>
            Chaque pesée devient une session consultable : score par
            rapport à l'objectif, écart avec la pesée précédente, rythme
            de progression estimé en kg/semaine.
          </p>
          <p>
            Retrouvez vos notes associées et modifiez ou supprimez une
            entrée en un clic si besoin.
          </p>
        </FeatureRowText>
        <FeatureRowVisual className="demo-preview-visual">
          <TrackingSessionsCard>
            <TrackingSessionsTitle>Sessions poids</TrackingSessionsTitle>
            <TrackingSessionList>
              {MOCK_SESSIONS.map((entry) => {
                const score = computeWeightScore(entry.current, TARGET_WEIGHT);
                const deltaToTarget = entry.current - TARGET_WEIGHT;
                const deltaVsPrevious = Number.isFinite(entry.previous)
                  ? entry.current - entry.previous
                  : null;

                return (
                  <TrackingSessionCard key={entry.id} className="landing-weight-session-entry" $borderColor={colors.border}>
                    <TrackingSessionHeader>
                      <SessionMain>
                        <TrackingSessionTitle>{formatSessionTitle(entry.at)}</TrackingSessionTitle>
                      </SessionMain>
                      <TrackingSessionScore>
                        <TrackingSessionScoreValue $color={score.color}>
                          {score.value !== null ? `${score.value}%` : '—'}
                        </TrackingSessionScoreValue>
                        <TrackingSessionScoreLabel>Cible</TrackingSessionScoreLabel>
                      </TrackingSessionScore>
                    </TrackingSessionHeader>

                    <TrackingSessionMetaRow>
                      <TrackingSessionMetaItem>Poids actuel : {formatKg(entry.current)}</TrackingSessionMetaItem>
                      <TrackingSessionMetaItem>Cible : {formatKg(TARGET_WEIGHT)}</TrackingSessionMetaItem>
                      <TrackingSessionMetaItem>Écart : {formatSignedKg(deltaToTarget)}</TrackingSessionMetaItem>
                      <TrackingSessionMetaItem>Mode : {modeLabel('lose')}</TrackingSessionMetaItem>
                      <TrackingSessionMetaItem>
                        Vs préc. : {deltaVsPrevious !== null ? formatSignedKg(deltaVsPrevious) : '—'}
                      </TrackingSessionMetaItem>
                      <TrackingSessionMetaItem>Rythme : 0.4 kg/sem</TrackingSessionMetaItem>
                    </TrackingSessionMetaRow>

                    {entry.note ? (
                      <SessionNote>
                        <SessionNoteLabel>
                          <FontAwesomeIcon icon={faNoteSticky} />
                          Note
                        </SessionNoteLabel>
                        <SessionNoteText>{entry.note}</SessionNoteText>
                      </SessionNote>
                    ) : null}

                    <TrackingSessionActions>
                      <SessionActionButton type="button">Modifier</SessionActionButton>
                      <SessionActionButton $variant="danger" type="button">Supprimer</SessionActionButton>
                    </TrackingSessionActions>
                  </TrackingSessionCard>
                );
              })}
            </TrackingSessionList>
          </TrackingSessionsCard>
        </FeatureRowVisual>
      </FeatureRow>
    </FullWidthSection>
  );
};

export default WeightDemo;
